import { Trans } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { setCookie } from '@tanstack/react-start/server';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { CheckCircle } from '@untitledui/icons/CheckCircle';
import { Loading02 } from '@untitledui/icons/Loading02';
import { useEffect, useState } from 'react';

import { LinkButton } from '@/components/base/buttons/button';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { DecorativeCircles } from '@/routes/participants/-components/decorative-circles';
import { SignupCarousel } from '@/routes/participants/-components/signup-carousel';
import { serverFetch } from '@/utils/server-fetch';
import {
  createSession,
  buildSessionCookie,
} from '@/utils/session/create-session';

const verifyEmailToken = createServerFn()
  .validator((data: { id: string; token: string }) => data)
  .handler(async ({ data: { id, token } }) => {
    try {
      const verifyResponse = await serverFetch({
        path: `/users/${id}`,
        method: 'PATCH',
        isServiceAdmin: true,
        body: JSON.stringify({ verificationToken: token }),
      });

      if (!verifyResponse.ok) {
        return { success: false as const };
      }

      const sessionId = await createSession(id, undefined, true);
      const cookie = buildSessionCookie(sessionId);
      setCookie(cookie.name, cookie.value, cookie.options);

      return { success: true as const };
    } catch (error) {
      console.error('Email verification error:', error);
      return { success: false as const };
    }
  });

export const Route = createFileRoute(
  '/participants/verify-email/$id/account/$token',
)({
  component: VerifyEmailTokenPage,
});

type VerifyState = 'verifying' | 'success' | 'error';

function VerifyEmailTokenPage() {
  const { id, token } = Route.useParams();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const [state, setState] = useState<VerifyState>('verifying');

  useEffect(() => {
    let cancelled = false;
    let redirectTimeout: ReturnType<typeof setTimeout> | undefined;

    const run = async () => {
      try {
        const result = await verifyEmailToken({ data: { id, token } });
        if (cancelled) return;
        if (result.success) {
          setState('success');
          redirectTimeout = setTimeout(() => {
            if (!cancelled)
              void navigate({
                to: '/participants/onboarding',
                reloadDocument: true,
              });
          }, 1500);
        } else {
          setState('error');
        }
      } catch {
        if (cancelled) return;
        setState('error');
      }
    };
    void run();

    return () => {
      cancelled = true;
      if (redirectTimeout) clearTimeout(redirectTimeout);
    };
  }, [id, token, navigate]);

  useEffect(() => {
    if (state === 'success') {
      analytics?.track({
        action: 'verified',
        actionSubject: 'email',
      });
    }
  }, [state, analytics]);

  return (
    <AuthLayout rightPanel={<SignupCarousel />}>
      <div className="flex flex-col items-center text-center">
        <DecorativeCircles>
          {/* Icon badge */}
          {state === 'verifying' && (
            <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-brand-solid">
              <Loading02 className="size-7 animate-spin text-white" />
            </div>
          )}

          {state === 'success' && (
            <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-success-solid duration-500 animate-in fade-in zoom-in-50">
              <CheckCircle className="size-7 text-white" />
            </div>
          )}

          {state === 'error' && (
            <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-error-solid">
              <AlertCircle className="size-7 text-white" />
            </div>
          )}
        </DecorativeCircles>

        {/* Heading */}
        <h1 className="z-10 mt-2 font-heading text-display-sm font-semibold text-primary">
          {state === 'verifying' && (
            <span>
              <Trans>Verifying your email...</Trans>
            </span>
          )}
          {state === 'success' && (
            <span>
              <Trans>Email verified!</Trans>
            </span>
          )}
          {state === 'error' && (
            <span>
              <Trans>Verification failed</Trans>
            </span>
          )}
        </h1>

        {/* Body text */}
        <p className="z-10 mt-3 text-md text-primary">
          {state === 'verifying' && (
            <span>
              <Trans>Please wait while we verify your email address.</Trans>
            </span>
          )}
          {state === 'success' && (
            <span>
              <Trans>
                Your email has been successfully verified. You can now access
                your dashboard and start exploring projects.
              </Trans>
            </span>
          )}
          {state === 'error' && (
            <span>
              <Trans>
                We couldn&apos;t verify your email. The link may have expired or
                already been used. Please try logging in or request a new
                verification email.
              </Trans>
            </span>
          )}
        </p>

        {/* Action button — only for error state */}
        {state === 'error' && (
          <div className="z-10 mt-8">
            <LinkButton color="primary" size="lg" to="/participants/login">
              <Trans>Go to login</Trans>
            </LinkButton>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
